import { useParams, Link, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowRight, Target, TrendingUp, Users } from "lucide-react";
import { SeasonCountdown, Countdown } from "../components/SeasonCountdown";

const LEAGUES = {
  "premier-league-predictor": { name: "Premier League", note: "England's top flight", kind: "rivalries", matchups: [["Manchester United", "Manchester City", "Manchester Derby"], ["Arsenal", "Tottenham", "North London Derby"], ["Liverpool", "Everton", "Merseyside Derby"]] },
  "la-liga-predictor": { name: "La Liga", note: "Spain's top division", kind: "rivalries", matchups: [["Real Madrid", "Barcelona", "El Clásico"], ["Atlético Madrid", "Real Madrid", "Madrid Derby"], ["Sevilla", "Real Betis", "Seville Derby"]] },
  "serie-a-predictor": { name: "Serie A", note: "Italy's top division", kind: "rivalries", matchups: [["Inter", "AC Milan", "Derby della Madonnina"], ["Juventus", "Inter", "Derby d'Italia"], ["Roma", "Lazio", "Derby della Capitale"]] },
  "bundesliga-predictor": { name: "Bundesliga", note: "Germany's top division", kind: "rivalries", matchups: [["Bayern Munich", "Borussia Dortmund", "Der Klassiker"], ["Borussia Dortmund", "Schalke 04", "Revierderby"], ["1. FC Köln", "Borussia Mönchengladbach", "Rhineland Derby"]] },
  "ligue-1-predictor": { name: "Ligue 1", note: "France's top division", kind: "rivalries", matchups: [["Paris Saint-Germain", "Marseille", "Le Classique"], ["Lyon", "Saint-Étienne", "Derby du Rhône"], ["Nice", "Monaco", "Côte d'Azur Derby"]] },
  "eredivisie-predictor": { name: "Eredivisie", note: "the Netherlands' top division", kind: "rivalries", matchups: [["Ajax", "Feyenoord", "De Klassieker"], ["PSV", "Ajax", "The Topper"], ["Feyenoord", "Sparta Rotterdam", "Rotterdam Derby"]] },
  "saudi-pro-league-predictor": { name: "Saudi Pro League", note: "Saudi Arabia's top division", kind: "rivalries", matchups: [["Al Hilal", "Al Nassr", "Riyadh Derby"], ["Al Ittihad", "Al Ahli", "Jeddah Derby"], ["Al Hilal", "Al Ittihad", "The Clásico"]] },
  "champions-league-predictor": { name: "UEFA Champions League", note: "Europe's elite club competition", kind: "ties", matchups: [["Real Madrid", "Manchester City", "Blockbuster tie"], ["Bayern Munich", "Paris Saint-Germain", "Heavyweight clash"], ["Barcelona", "Inter", "European classic"]] },
  "europa-league-predictor": { name: "UEFA Europa League", note: "Europe's second-tier club competition", kind: "ties", matchups: [["Roma", "Manchester United", "Marquee tie"], ["Villarreal", "Ajax", "European night"], ["Lazio", "Porto", "Continental clash"]] },
  "scottish-premiership-predictor": { name: "Scottish Premiership", note: "Scotland's top flight", kind: "rivalries", matchups: [["Celtic", "Rangers", "The Old Firm"], ["Hearts", "Hibernian", "Edinburgh Derby"], ["Dundee", "Dundee United", "Dundee Derby"]] },
  "super-lig-predictor": { name: "Süper Lig", note: "Turkey's top division", kind: "rivalries", matchups: [["Galatasaray", "Fenerbahçe", "Intercontinental Derby"], ["Beşiktaş", "Galatasaray", "Istanbul Derby"], ["Trabzonspor", "Fenerbahçe", "Karadeniz Clash"]] },
  "argentine-primera-predictor": { name: "Argentine Primera División", note: "Argentina's top flight", kind: "rivalries", matchups: [["Boca Juniors", "River Plate", "El Superclásico"], ["Racing Club", "Independiente", "Avellaneda Derby"], ["San Lorenzo", "Huracán", "Derby of the South"]] },
};

// Dedicated high-traffic derby landing pages. leagueSlug is null when we don't
// (yet) have a full predictor page for that competition.
const DERBIES = {
  "el-clasico": { name: "El Clásico", home: "Real Madrid", away: "Barcelona", league: "La Liga", leagueSlug: "la-liga-predictor" },
  "manchester-derby": { name: "Manchester Derby", home: "Manchester United", away: "Manchester City", league: "Premier League", leagueSlug: "premier-league-predictor" },
  "north-london-derby": { name: "North London Derby", home: "Arsenal", away: "Tottenham", league: "Premier League", leagueSlug: "premier-league-predictor" },
  "merseyside-derby": { name: "Merseyside Derby", home: "Liverpool", away: "Everton", league: "Premier League", leagueSlug: "premier-league-predictor" },
  "milan-derby": { name: "Derby della Madonnina", home: "Inter", away: "AC Milan", league: "Serie A", leagueSlug: "serie-a-predictor" },
  "derby-d-italia": { name: "Derby d'Italia", home: "Juventus", away: "Inter", league: "Serie A", leagueSlug: "serie-a-predictor" },
  "rome-derby": { name: "Derby della Capitale", home: "Roma", away: "Lazio", league: "Serie A", leagueSlug: "serie-a-predictor" },
  "der-klassiker": { name: "Der Klassiker", home: "Bayern Munich", away: "Borussia Dortmund", league: "Bundesliga", leagueSlug: "bundesliga-predictor" },
  "revierderby": { name: "Revierderby", home: "Borussia Dortmund", away: "Schalke 04", league: "Bundesliga", leagueSlug: "bundesliga-predictor" },
  "le-classique": { name: "Le Classique", home: "Paris Saint-Germain", away: "Marseille", league: "Ligue 1", leagueSlug: "ligue-1-predictor" },
  "de-klassieker": { name: "De Klassieker", home: "Ajax", away: "Feyenoord", league: "Eredivisie", leagueSlug: "eredivisie-predictor" },
  "madrid-derby": { name: "Madrid Derby", home: "Atlético Madrid", away: "Real Madrid", league: "La Liga", leagueSlug: "la-liga-predictor" },
  "seville-derby": { name: "Seville Derby", home: "Real Betis", away: "Sevilla", league: "La Liga", leagueSlug: "la-liga-predictor" },
  "old-firm": { name: "The Old Firm", home: "Celtic", away: "Rangers", league: "the Scottish Premiership", leagueSlug: "scottish-premiership-predictor" },
  "superclasico": { name: "El Superclásico", home: "Boca Juniors", away: "River Plate", league: "the Argentine Primera División", leagueSlug: "argentine-primera-predictor" },
  "istanbul-derby": { name: "The Intercontinental Derby", home: "Galatasaray", away: "Fenerbahçe", league: "the Turkish Süper Lig", leagueSlug: "super-lig-predictor" },
};

const Header = () => (
  <header className="border-b-2 border-foreground">
    <div className="max-w-3xl mx-auto px-4 py-5 flex items-center justify-between">
      <Link to="/" className="font-display text-3xl tracking-tight leading-none">GAFFER<span className="text-primary">.</span></Link>
      <Link to="/register"><Button size="sm" className="rounded-none bg-primary text-primary-foreground uppercase font-bold" data-testid="lp-signup-btn">Play free <ArrowRight size={14} className="ms-1" /></Button></Link>
    </div>
  </header>
);

const ScoringSection = ({ suffix }) => (
  <section className="mb-10" data-testid="lp-scoring">
    <h2 className="font-display text-2xl sm:text-3xl uppercase mb-4">How points work</h2>
    <div className="grid sm:grid-cols-3 gap-3">
      {[
        { pts: "3", label: "Exact score", desc: "Call the exact scoreline of a match" },
        { pts: "1.5", label: "Close", desc: "Right result, within a goal" },
        { pts: "1", label: "Correct result", desc: "Call the win, draw or loss" },
      ].map((s) => (
        <div key={s.label} className="border-2 border-border p-4">
          <div className="font-display text-4xl text-primary leading-none">{s.pts}<span className="text-lg text-muted-foreground"> pts</span></div>
          <div className="font-display uppercase text-lg mt-2">{s.label}</div>
          <p className="text-sm text-muted-foreground mt-1">{s.desc}</p>
        </div>
      ))}
    </div>
    <p className="text-sm text-muted-foreground mt-3">The sharper your {suffix} predictions, the higher you climb the global table.</p>
  </section>
);

const ScoreInput = () => (
  <div className="flex items-center gap-1 shrink-0">
    <span className="w-9 h-9 grid place-items-center border-2 border-border font-data font-bold text-lg text-muted-foreground">–</span>
    <span className="text-muted-foreground font-data">:</span>
    <span className="w-9 h-9 grid place-items-center border-2 border-border font-data font-bold text-lg text-muted-foreground">–</span>
  </div>
);

const FaqSection = ({ title, faqs }) => (
  <section className="mt-10" data-testid="lp-faq">
    <h2 className="font-display text-2xl sm:text-3xl uppercase mb-4">{title}</h2>
    <div className="space-y-4">
      {faqs.map((f, i) => (
        <div key={i} className="border-b border-border pb-4" data-testid={`lp-faq-${i}`}>
          <h3 className="font-display uppercase text-lg mb-1">{f.q}</h3>
          <p className="text-sm text-muted-foreground">{f.a}</p>
        </div>
      ))}
    </div>
  </section>
);

const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

function DerbyPage({ slug, d }) {
  const title = `${d.name} Predictor — Predict ${d.home} vs ${d.away} | GAFFER`;
  const description = `Predict the ${d.name} on GAFFER — call the ${d.home} vs ${d.away} scoreline for free, earn points for accuracy and beat your mates. Part of the free ${d.league} score predictor.`;
  const canonical = `https://thefootballgaffer.com/predictor/${slug}`;
  const artName = /^(the|el|la|le|de|der|il)\b/i.test(d.name) ? d.name : `the ${d.name}`;
  const faqs = [
    { q: `Is ${artName} predictor free?`, a: `Yes — predicting ${artName} on GAFFER is completely free. Sign up in under a minute and call the ${d.home} vs ${d.away} scoreline.` },
    { q: `How do I predict the ${d.home} vs ${d.away} score?`, a: `Enter your predicted scoreline before kick-off. Score 3 points for an exact result, 1.5 for the right result within a goal, and 1 for calling the winner.` },
    { q: `Can I predict ${artName} with my mates?`, a: `Absolutely — start a private banter league, invite your friends and see who calls ${artName} best, then keep it going across the whole ${d.league} season.` },
  ];
  const relatedDerbies = Object.entries(DERBIES).filter(([s, x]) => d.leagueSlug && x.leagueSlug === d.leagueSlug && s !== slug);

  const [nextDate, setNextDate] = useState(null);
  useEffect(() => {
    let ok = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/derbies`)
      .then((r) => r.json())
      .then((data) => {
        if (!ok) return;
        const found = (data.derbies || []).find((x) => x.slug === slug);
        if (found && found.next_date) setNextDate(new Date(found.next_date));
      })
      .catch(() => {});
    return () => { ok = false; };
  }, [slug]);

  return (
    <div className="min-h-screen bg-background text-foreground grain">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <script type="application/ld+json">{JSON.stringify(faqLd(faqs))}</script>
      </Helmet>
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-12" data-testid="derby-predictor">
        {nextDate
          ? <Countdown target={nextDate} label={`${d.name} kicks off in`} liveLabel={`${d.name} — kicking off now`} className="mb-5" testid="derby-countdown" />
          : <SeasonCountdown className="mb-5" />}
        <p className="overline text-primary mb-2">{d.home} vs {d.away}</p>
        <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mb-4">{d.name} predictor</h1>
        <p className="text-base text-muted-foreground mb-8">
          Think you can call {artName}? Predict the {d.home} vs {d.away} scoreline for free on GAFFER, earn points
          for how close you get and settle the argument with your mates. It's one of the biggest fixtures in{" "}
          {d.leagueSlug
            ? <><Link to={`/predictor/${d.leagueSlug}`} className="text-primary hover:underline">{d.league}</Link> — back your call.</>
            : <>{d.league} — back your call.</>}
        </p>

        {/* The fixture */}
        <section className="mb-10" data-testid="derby-fixture">
          <div className="border-2 border-foreground p-6">
            <p className="overline text-primary text-center mb-3 text-[10px]">{d.name}</p>
            <div className="flex items-center gap-3">
              <span className="flex-1 text-right font-display uppercase text-xl sm:text-2xl leading-none">{d.home}</span>
              <ScoreInput />
              <span className="flex-1 font-display uppercase text-xl sm:text-2xl leading-none">{d.away}</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2 font-data">Sign up to lock in your {d.name} prediction against the real fixture.</p>
        </section>

        <ScoringSection suffix={d.name} />

        <div className="border-2 border-foreground p-6 text-center">
          <h2 className="font-display text-3xl uppercase mb-2">Predict {artName} — free</h2>
          <p className="text-muted-foreground mb-4">Call the {d.home} vs {d.away} score in under a minute.</p>
          <Link to="/register"><Button className="rounded-none bg-primary text-primary-foreground uppercase font-bold" data-testid="lp-cta-btn">Start predicting <ArrowRight size={15} className="ms-1" /></Button></Link>
        </div>

        <FaqSection title={`${d.name} predictor FAQ`} faqs={faqs} />

        {relatedDerbies.length > 0 && (
          <div className="mt-8">
            <p className="overline text-primary mb-2 text-[10px]">More {d.league} rivalries</p>
            <div className="flex flex-wrap gap-2">
              {relatedDerbies.map(([s, x]) => (
                <Link key={s} to={`/predictor/${s}`} data-testid={`derby-link-${s}`}
                  className="border border-border px-3 py-1.5 text-sm font-data hover:border-primary hover:text-primary transition-colors">{x.name}</Link>
              ))}
            </div>
          </div>
        )}

        {d.leagueSlug && (
          <p className="mt-8 text-sm text-muted-foreground">
            Prefer the full table? Try the <Link to={`/predictor/${d.leagueSlug}`} className="text-primary hover:underline">{d.league} predictor</Link> and predict every fixture, week by week.
          </p>
        )}
        <footer className="mt-10 pt-6 border-t border-border text-xs text-muted-foreground font-data">
          <Link to="/" className="hover:text-primary">← Back to home</Link>
        </footer>
      </main>
    </div>
  );
}

function LeaguePage({ slug, lg }) {
  const title = `${lg.name} Predictor — Free ${lg.name} Score Predictions | GAFFER`;
  const description = `Predict every ${lg.name} scoreline for free on GAFFER. Call each result week by week, build your predicted ${lg.name} table, earn points for accuracy and take on your mates in a private banter league.`;
  const canonical = `https://thefootballgaffer.com/predictor/${slug}`;
  const fixtures = lg.matchups;
  const kindLabel = lg.kind === "ties" ? "ties" : "rivalries";
  const faqs = [
    { q: `Is the ${lg.name} predictor free to play?`, a: `Yes — GAFFER's ${lg.name} predictor is completely free. Sign up in under a minute and start predicting every scoreline. No card needed.` },
    { q: `How do points work when I predict ${lg.name} scores?`, a: `You score 3 points for an exact scoreline, 1.5 points for the right result within a goal, and 1 point for calling the correct result. The more accurate you are, the higher you climb.` },
    { q: `Can I play a ${lg.name} predictor league with my friends?`, a: `Absolutely. Create a private banter league, invite your mates and go head-to-head across the whole ${lg.name} season for season-long bragging rights.` },
  ];
  // Dedicated derby pages that belong to this league.
  const leagueDerbies = Object.entries(DERBIES).filter(([, x]) => x.leagueSlug === slug);

  return (
    <div className="min-h-screen bg-background text-foreground grain">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <script type="application/ld+json">{JSON.stringify(faqLd(faqs))}</script>
      </Helmet>
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-12" data-testid="league-predictor">
        <SeasonCountdown className="mb-5" />
        <p className="overline text-primary mb-2">{lg.name} predictions</p>
        <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mb-4">{lg.name} predictor</h1>
        <p className="text-base text-muted-foreground mb-6">
          Predict every {lg.name} scoreline for free on GAFFER. {lg.name} is {lg.note} — call each result week by
          week, earn points for accuracy, and watch your predicted {lg.name} table take shape. Back your football
          knowledge against fans worldwide and settle it with your mates in a private banter league.
        </p>

        <div className="space-y-6 mb-10">
          <p className="text-muted-foreground flex items-start gap-2"><Target size={18} className="text-primary mt-0.5 shrink-0" /> <span>Predict the exact score of every {lg.name} fixture — real bookmaker win probabilities help you weigh up each match.</span></p>
          <p className="text-muted-foreground flex items-start gap-2"><TrendingUp size={18} className="text-primary mt-0.5 shrink-0" /> <span>Climb the worldwide accuracy leaderboard and prove you know {lg.name} better than anyone.</span></p>
          <p className="text-muted-foreground flex items-start gap-2"><Users size={18} className="text-primary mt-0.5 shrink-0" /> <span>Start a {lg.name} banter league and take on your friends all season long.</span></p>
        </div>

        <ScoringSection suffix={lg.name} />

        {/* Sample fixtures */}
        <section className="mb-10" data-testid="lp-sample-fixtures">
          <h2 className="font-display text-2xl sm:text-3xl uppercase mb-1">Famous {lg.name} {kindLabel} to predict</h2>
          <p className="text-sm text-muted-foreground mb-4">Call the big ones — here's the kind of {lg.name} {kindLabel} you'll be predicting:</p>
          <div className="border-2 border-foreground divide-y divide-border">
            {fixtures.map(([home, away, tie], i) => (
              <div key={i} className="px-4 py-3" data-testid={`lp-fixture-${i}`}>
                <p className="overline text-primary text-center mb-1.5 text-[10px]" data-testid={`lp-fixture-name-${i}`}>{tie}</p>
                <div className="flex items-center gap-3">
                  <span className="flex-1 text-right font-medium truncate">{home}</span>
                  <ScoreInput />
                  <span className="flex-1 font-medium truncate">{away}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2 font-data">Examples only — your predictions run against the real {lg.name} schedule once you sign up.</p>
        </section>

        {leagueDerbies.length > 0 && (
          <section className="mb-10" data-testid="lp-derby-links">
            <h2 className="font-display text-xl sm:text-2xl uppercase mb-3">Predict the big {lg.name} derbies</h2>
            <div className="flex flex-wrap gap-2">
              {leagueDerbies.map(([s, x]) => (
                <Link key={s} to={`/predictor/${s}`} data-testid={`lp-derby-link-${s}`}
                  className="group flex items-center gap-1.5 border-2 border-border px-3 py-2 text-sm font-display uppercase leading-none hover:border-primary hover:text-primary transition-colors">
                  {x.name} <ArrowRight size={13} className="opacity-60 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="border-2 border-foreground p-6 text-center">
          <h2 className="font-display text-3xl uppercase mb-2">Predict the {lg.name} — free</h2>
          <p className="text-muted-foreground mb-4">Make your first {lg.name} predictions in under a minute.</p>
          <Link to="/register"><Button className="rounded-none bg-primary text-primary-foreground uppercase font-bold" data-testid="lp-cta-btn">Start predicting <ArrowRight size={15} className="ms-1" /></Button></Link>
        </div>

        <FaqSection title={`${lg.name} predictor FAQ`} faqs={faqs} />

        <p className="mt-8 text-sm text-muted-foreground">
          GAFFER also covers <Link to="/how-it-works" className="text-primary hover:underline">many other leagues</Link> including La Liga, Serie A, the Champions League and more.
        </p>
        <footer className="mt-10 pt-6 border-t border-border text-xs text-muted-foreground font-data">
          <Link to="/" className="hover:text-primary">← Back to home</Link>
        </footer>
      </main>
    </div>
  );
}

export default function LeaguePredictor() {
  const { slug } = useParams();
  const lg = LEAGUES[slug];
  if (lg) return <LeaguePage slug={slug} lg={lg} />;
  const d = DERBIES[slug];
  if (d) return <DerbyPage slug={slug} d={d} />;
  return <Navigate to="/how-it-works" replace />;
}
